export enum BusinessLicenseType {
  GeneralBusinessLicense = 'General_Business_License',
  ProfessionalLicense = 'Professional_License',
  VendorPermit = 'Vendor_Permit',
  FoodSafetyCertification = 'Food_Safety_Certification'
}

export enum AgreementType {
  Quality = 'Quality',
  PrivacyPolicy = 'Privacy_Policy',
  EULA = 'EULA'
}

export enum BusinessRole {
  HeadChef = 'HeadChef',
  SousChef = 'SousChef',
  BusinessOwner = 'BusinessOwner',
  Other = 'Other'
}

export enum BusinessRoleTranslation {
  HeadChef = 'Head_Chef',
  SousChef = 'Sous_Chef',
  BusinessOwner = 'Business_Owner',
  Other = 'Other'
}

export enum BusinessType {
  BrickAndMortarRestaurant = 'BrickAndMortarRestaurant',
  GhostKitchen = 'GhostKitchen',
  PrePackagedManufacturer = 'PrePackagedManufacturer',
  PersonalChef = 'PersonalChef',
  Catering = 'Catering'
}

export enum BusinessTypeTranslation {
  BrickAndMortarRestaurant = 'Brick_And_Mortar_Restaurant',
  GhostKitchen = 'Ghost_Kitchen',
  PrePackagedManufacturer = 'Pre_Packaged_Manufacturer',
  PersonalChef = 'Personal_Chef',
  Catering = 'Catering'
}

export enum FulfillmentType {
  DineIn = 'DineIn',
  TakeOut = 'TakeOut',
  ProducerDelivery = 'ProducerDelivery',
  YoutopiaDelivery = 'YoutopiaDelivery',
  PrivateEventsCatering = 'PrivateEventsCatering',
  InHomePrivateChef = 'InHomePrivateChef'
}

export enum FulfillmentTypeTranslation {
  DineIn = 'Dine_In',
  TakeOut = 'TakeOut',
  ProducerDelivery = 'Producer_Delivery',
  YoutopiaDelivery = 'Youtopia_Delivery',
  PrivateEventsCatering = 'Private_Events_Catering',
  InHomePrivateChef = 'In_Home_Private_Chef'
}

export enum BusinessCertificationTypeTranslation {
  Kosher = 'Kosher',
  GlutenFree = 'Gluten_Free',
  NutFree = 'Nut-Free',
  Halal = 'Halal'
}

export interface BusinessLicense {
  businessLicenseId: number;
  businessLicenseTypeId: number;
  establishmentId: number;
  issuingAuthorityName: string;
  issuingAuthorityCity: string;
  issuingAuthorityState: string;
  issuingAuthorityCounty: string;
  issueDate: string;
  expirationDate: string;
  licenseNumber: string;
}

export interface Agreement {
  type: AgreementType;
  version: string;
  signedBy: string;
  signedAt: Date;
}

export interface Address {
  addressLineOne: string;
  addressLineTwo: string;
  city: string;
  state: string;
  zip: string;
  longitude: number;
  latitude: number;
  [key: string]: any;
}

export interface Establishment {
  establishmentGuid: string;
  name: string;
  phone: string;
  email: string;
  imageUrl: string;
  address: Address;
  mailingAddress: Address;
  fulfillmentTypes: { fulfillmentTypeId: FulfillmentType }[];
  businessLicenses: BusinessLicense[];
  taxId: string;
  agreements: Agreement[];
  fulfillmentDeliveryScheduleOccurrences: any[];
  inventoryStockSettings: any[];
  inventoryPriceSettings: any[];
  inventoryScheduleAheadPriceDiscounts: any[];
}

export interface TeamMember {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  businessRoleName: BusinessRole;
}

/** The producer lifecycle status (mirror Youtopia.Producers.Data.Enums.ProducerStatus). */
export enum ProducerStatus {
  Deactivated = 'Deactivated',
  Lead = 'Lead',
  Pending = 'Pending',
  Ready = 'Ready',
  Active = 'Active',
  Suspended = 'Suspended'
}

export interface Producer {
  producerGuid: string;
  // The lifecycle status the /me read returns; optional on the model so partial fixtures/mocks need not set it, and
  // an absent value reads as "not Active" (the status-based guards route such a producer to onboarding).
  status?: ProducerStatus;
  name: string;
  email: string;
  url: string;
  imageUrl: string;
  establishments: Establishment[];
  marketingBlurb: string;
  phone: string;
  address: Address;
  mailingAddress: Address;
  learningCompleted: boolean;
  lookupBusinessTypes: any[];
  producerUsers: ProducerUser[];
  teamMembers: TeamMember[];
  inventoryPriceSettings: any[];
  inventoryStockSettings: any[];
  promoCodeUsages: any[];
  youtopiaOrders: any[];
}

export interface ProducerUser {
  producerUserGuid: string;
  // The owning producer's guid, surfaced by the self-read so the FE can self-read the producer by id (MLP-1517).
  producerGuid: string;
  firstName: string;
  lastName: string;
  email: string;
  imageUrl: string;
  businessRole: string;
}

export interface DeliveryTimeWindow {
  startTime: string;
  endTime: string;
  maxDeliveriesInWindow: number;
}

export interface DaySchedule {
  enabled: boolean;
  windows: DeliveryTimeWindow[];
}

export interface DeliveryConfig {
  schedule: DaySchedule[];
}

export enum OnboardingStepType {
  Onboarding = 'onboarding',
  Vendor = 'vendor'
}

export interface FormattedDaySchedule {
  dayOfWeek: string;
  schedules: DeliveryTimeWindow[];
}

export interface EstablishmentSearchResult {
  id: string;
  phoneNumber: string;
  displayName: {
    text: string;
    languageCode: string;
  };
  address: {
    formattedAddress: string;
    streetNumber: string;
    route: string;
    neighborhood: string | null;
    city: string;
    county: string;
    state: string;
    zipCode: string;
    country: string;
    latitude: number;
    longitude: number;
  };
  websiteUri?: string;
}

export interface EstablishmentSearchResponse {
  establishments: EstablishmentSearchResult[];
}

export interface EstablishmentAmenities {
  allowsDogs: boolean;
  curbsidePickup: boolean;
  delivery: boolean;
  dineIn: boolean;
  evChargeOptions: boolean;
  freeParkingLot: boolean;
  freeStreetParking: boolean;
  goodForChildren: boolean;
  goodForGroups: boolean;
  goodForWatchingSports: boolean;
  liveMusic: boolean;
  menuForChildren: boolean;
  outdoorSeating: boolean;
  reservable: boolean;
  restroom: boolean;
  servesBeer: boolean;
  servesBreakfast: boolean;
  servesBrunch: boolean;
  servesCocktails: boolean;
  servesCoffee: boolean;
  servesDessert: boolean;
  servesDinner: boolean;
  servesLunch: boolean;
  servesVegetarianFood: boolean;
  servesWine: boolean;
  takeout: boolean;
  valetParking: boolean;
}

export interface ProducerFacilityCertification {
  kosherCertified: boolean;
  glutenFreeCertified: boolean;
  nutFreeCertified: boolean;
  halalCertified: boolean;
}

export interface CreateProducerUserDto {
  firstName: string;
  lastName: string;
  contactEmail: string;
  businessRole?: BusinessRole;
}

export interface CreateEstablishmentDto {
  name: string;
  phone: string;
  email: string;
  address: Address;
  mailingAddress: Address;
}

export interface CreateProducerDto {
  name: string;
  email: string;
  phone: string;
  businessTypes: BusinessType[];
  address: Address;
  producerUser: CreateProducerUserDto;
  establishment: CreateEstablishmentDto;
}

export interface UpdateProducerImageDto {
  image: File;
}

export interface BusinessHoursInterval {
  openTime: string;
  closeTime: string;
  notes: string;
}

export interface BusinessHoursDaySchedule {
  dayOfWeek: number; // Day of week (0 = Sunday, 1 = Monday, ... 6 = Saturday)
  intervals: BusinessHoursInterval[];
}

export interface EstablishmentBusinessHours {
  is24Hours: boolean;
  isTemporarilyClosed: boolean;
  temporaryClosureNote: string;
  dataSource: string;
  daySchedules: BusinessHoursDaySchedule[];
}
